import { useState, useCallback, type KeyboardEvent } from 'react';
import { Box, IconButton } from '@radix-ui/themes';
import { PaperPlaneIcon } from '@radix-ui/react-icons';
import styled from '@emotion/styled';

const Wrapper = styled.div`
  position: relative;
  border: 1px solid var(--gray-a6);
  border-radius: 12px;
  background: var(--color-background);
  transition: border-color 0.15s;

  &:focus-within {
    border-color: var(--accent-8);
  }
`;

const Textarea = styled.textarea`
  display: block;
  width: 100%;
  min-height: 80px;
  max-height: 200px;
  resize: none;
  border: none;
  outline: none;
  background: transparent;
  padding: 12px 48px 12px 16px;
  font-family: inherit;
  font-size: 14px;
  line-height: 1.5;
  color: var(--gray-12);

  &::placeholder {
    color: var(--gray-a8);
  }
`;

interface MessageInputProps {
  onSend: (message: string) => void;
  placeholder?: string;
  disabled?: boolean;
}

export function MessageInput({
  onSend,
  placeholder = 'Ask Dutchie Agent anything...',
  disabled,
}: MessageInputProps) {
  const [value, setValue] = useState('');

  const handleSend = useCallback(() => {
    const trimmed = value.trim();
    if (!trimmed) {
      return;
    }
    onSend(trimmed);
    setValue('');
  }, [value, onSend]);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        handleSend();
      }
    },
    [handleSend],
  );

  return (
    <Wrapper>
      <Textarea
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        disabled={disabled}
      />
      <Box style={{ position: 'absolute', right: 8, bottom: 8 }}>
        <IconButton
          variant="soft"
          color="gray"
          size="2"
          radius="full"
          onClick={handleSend}
          disabled={disabled || !value.trim()}
        >
          <PaperPlaneIcon />
        </IconButton>
      </Box>
    </Wrapper>
  );
}
